import React, { useState, useEffect } from "react";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const TYRO_LOGO_URL =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSgKP85jSyxrQYV55sd4pMh10SIuHrFa0NCoQ&s";

const SetupTyroModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    MerchantID: "",
    APIPassword: "",
    ReportingAPIPassword: "",
  });
  const [selectedChemist, setSelectedChemist] = useState(null);
  const [chemists, setChemists] = useState([]);
  const [isFetchingChemists, setIsFetchingChemists] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchChemists();
    }
  }, [isOpen]);

  const fetchChemists = async () => {
    setIsFetchingChemists(true);
    try {
      const response = await phappsRequest(API_PATHS.CHEMIST_LIST, "GET");
      if (response.success && Array.isArray(response.chemists)) {
        setChemists(response.chemists);
      } else {
        setStatus({ type: "error", message: "Failed to load chemists list" });
      }
    } catch (error) {
      console.error(error);
      setStatus({ type: "error", message: "Error loading chemists" });
    } finally {
      setIsFetchingChemists(false);
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus(null);

    if (!selectedChemist) {
      setStatus({ type: "error", message: "Please select a chemist" });
      setIsLoading(false);
      return;
    }

    try {
      const payload = {
        ChemistID: selectedChemist.chemistID,
        MerchantID: formData.MerchantID,
        APIPassword: formData.APIPassword,
        ReportingAPIPassword: formData.ReportingAPIPassword,
      };

      const response = await phappsRequest(
        API_PATHS.SETUP_HALOPAY,
        "POST",
        payload
      );

      if (response.status) {
        setStatus({ type: "success", message: "Tyro setup saved successfully" });
      } else {
        setStatus({ type: "error", message: "Tyro setup failed" });
      }
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 my-8">
        <div className="flex items-center gap-4 mb-4">
          <img
            src={TYRO_LOGO_URL}
            alt="Tyro"
            className="h-10 w-auto object-contain"
          />
          <h2 className="text-xl font-bold text-gray-900">
            Setup Tyro credentials
          </h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Enter the Tyro merchant and API credentials for the selected chemist.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Chemist
            </label>
            <Autocomplete
              options={chemists}
              getOptionLabel={(option) =>
                `${option.chemistName} (ID: ${option.chemistID})`
              }
              value={selectedChemist}
              onChange={(event, newValue) => {
                setSelectedChemist(newValue);
              }}
              loading={isFetchingChemists}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search chemist..."
                  variant="outlined"
                  size="small"
                  required={!selectedChemist}
                />
              )}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Merchant ID
            </label>
            <input
              type="text"
              name="MerchantID"
              value={formData.MerchantID}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              API password
            </label>
            <input
              type="password"
              name="APIPassword"
              value={formData.APIPassword}
              onChange={handleChange}
              autoComplete="new-password"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Reporting API password
            </label>
            <input
              type="password"
              name="ReportingAPIPassword"
              value={formData.ReportingAPIPassword}
              onChange={handleChange}
              autoComplete="new-password"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
              required
            />
          </div>

          {status && (
            <div
              className={`p-3 rounded-lg ${
                status.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {status.message}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? "Saving..." : "Save credentials"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetupTyroModal;
